import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get language from localStorage
  const language = localStorage.getItem('language') || localStorage.getItem('selectedLanguage') || 'lv';

  const translations = {
    title: {
      en: 'Reset Password',
      ru: 'Сбросить пароль',
      lv: 'Atiestatīt paroli'
    },
    subtitle: {
      en: 'Enter your new password',
      ru: 'Введите новый пароль',
      lv: 'Ievadiet jauno paroli'
    },
    newPassword: {
      en: 'New Password',
      ru: 'Новый пароль',
      lv: 'Jaunā parole'
    },
    confirmPassword: {
      en: 'Confirm Password',
      ru: 'Подтвердите пароль',
      lv: 'Apstipriniet paroli'
    },
    resetButton: {
      en: 'Reset Password',
      ru: 'Сбросить пароль',
      lv: 'Atiestatīt paroli'
    },
    backToLogin: {
      en: 'Back to Login',
      ru: 'Вернуться к входу',
      lv: 'Atpakaļ uz pieteikšanos'
    },
    passwordsNoMatch: {
      en: 'Passwords do not match',
      ru: 'Пароли не совпадают',
      lv: 'Paroles nesakrīt'
    },
    passwordTooShort: {
      en: 'Password must be at least 6 characters',
      ru: 'Пароль должен содержать не менее 6 символов',
      lv: 'Parolei jābūt vismaz 6 rakstzīmēm'
    },
    successTitle: {
      en: 'Password Reset Successful!',
      ru: 'Пароль успешно сброшен!',
      lv: 'Parole veiksmīgi atiestatīta!'
    },
    successMessage: {
      en: 'Your password has been reset successfully. You can now log in with your new password.',
      ru: 'Ваш пароль был успешно сброшен. Теперь вы можете войти с новым паролем.',
      lv: 'Jūsu parole ir veiksmīgi atiestatīta. Tagad varat pierakstīties ar jauno paroli.'
    },
    goToLogin: {
      en: 'Go to Login',
      ru: 'Перейти к входу',
      lv: 'Doties uz pieteikšanos'
    },
    invalidToken: {
      en: 'Invalid or expired reset link',
      ru: 'Недействительная или истекшая ссылка для сброса',
      lv: 'Nederīga vai beigusies atiestatīšanas saite'
    }
  };

  useEffect(() => {
    if (!token) {
      toast.error(translations.invalidToken[language]);
      navigate('/login');
    }
  }, [token, navigate, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error(translations.passwordTooShort[language]);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(translations.passwordsNoMatch[language]);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || translations.invalidToken[language];
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="rz-page rz-form min-h-screen flex items-center justify-center p-5">
        <div className="w-full max-w-md">
          <div className="bg-white border border-[#E7E5E4] p-8 sm:p-10">
            {/* Italian flag logo */}
            <p className="rz-eyebrow text-center m-0 mb-5">Roze di Napoli</p>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#F5F2EC] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#1C1917]" />
              </div>
              <h1 className="font-display text-[2rem] leading-tight text-[#1C1917] mb-3">{translations.successTitle[language]}</h1>
              <p className="text-[#57534E] mb-6">{translations.successMessage[language]}</p>
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full rz-btn !rounded-none"
              >
                {translations.goToLogin[language]}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rz-page rz-form min-h-screen flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Button
          variant="ghost" style={{minHeight:44}}
          onClick={() => navigate('/login')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {translations.backToLogin[language]}
        </Button>

        <div className="bg-white border border-[#E7E5E4] p-8 sm:p-10">
          {/* Italian flag logo */}
          <p className="rz-eyebrow text-center m-0 mb-5">Roze di Napoli</p>

          <h1 className="font-display text-[2rem] leading-tight text-[#1C1917] text-center mb-3">{translations.title[language]}</h1>
          <p className="text-center text-[#57534E] mb-6">{translations.subtitle[language]}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                <Lock className="w-4 h-4 inline mr-2" />
                {translations.newPassword[language]}
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                <Lock className="w-4 h-4 inline mr-2" />
                {translations.confirmPassword[language]}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              className="w-full rz-btn !rounded-none"
              disabled={loading}
            >
              {loading ? '...' : translations.resetButton[language]}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
